import React, { useState, useEffect } from 'react';
import { Grid, Dialog, DialogContent } from '@material-ui/core';
import { consoleLogInDev, track } from 'shared';
import CloseIcon from '@material-ui/icons/Close';
import { PrimaryButton, Text } from 'library';
import { useUserContext } from 'UserContext';
import { FacebookIcon, FacebookShareButton } from 'react-share';
import { useLocation } from 'react-router-dom';
import { trackReferralEngagement } from 'api/UserApi';
import { Provider } from 'models';
import usePublicSessionTracking, { PublicSessionEventType } from 'parent-portal/shared/PublicSessionTracking';

interface IProps {
    open: boolean;
    onClose: () => void;
    provider: Provider;
}
export function ShareProviderProfile({ open, onClose, provider }: IProps) {
    const { user } = useUserContext();
    const location = useLocation();
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reid, setReid] = useState<number>();

    const { trackSession } = usePublicSessionTracking('Share Provider Profile Dialog');

    let link = `${window.location.origin}/provider/${provider.user.id}`;
    if (user) {
        link += `?referral=${user.referral_code}&sitter=${provider.user.id}`;
    }

    function closeDialog() {
        onClose();
        if (!user) {
            trackSession('DIALOG_CLOSE', 'Closed share provider dialog', PublicSessionEventType.User, {
                providerId: provider.id,
            });
        }
    }

    async function onCopyLinkClicked() {
        if (!user) {
            navigator.clipboard.writeText(link);
            setCopied(true);
            trackSession('CLICK', 'Clicked copy link', PublicSessionEventType.User, { providerId: provider.id });
            return;
        }

        setLoading(true);

        const linkToCopy = await getLinkToCopy();
        if (!!linkToCopy) {
            navigator.clipboard.writeText(linkToCopy);
        }
        track('Share provider link copied');
        setCopied(true);
        setLoading(false);
    }

    async function getLinkToCopy() {
        if (user?.id) {
            try {
                const response = await trackReferralEngagement(
                    user.id,
                    location.pathname,
                    'Share Provider Profile: Copy Link',
                );
                setReid(response.id);
                return `${link}&reid=${response.id}`;
            } catch (error) {
                consoleLogInDev(error);
                return link;
            }
        }
    }

    function onFBShareClick() {
        if (!user) {
            trackSession('CLICK', 'Clicked share provider to facebook', PublicSessionEventType.User, {
                providerId: provider.id,
            });
            return;
        }

        trackReferralEngagement(user.id, location.pathname, 'Share Provider Profile: Share via Facebook')
            .then((response) => {
                setReid(response.id);
            })
            .catch(consoleLogInDev);

        track('Share provider via facebook');
    }

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: '18px', padding: '2%' } }}
            fullWidth
            open={open}
            onClose={closeDialog}
        >
            <DialogContent>
                <Grid xs={12} container justify="flex-end">
                    <CloseIcon style={{ cursor: 'pointer' }} color="error" onClick={closeDialog} />
                </Grid>
                <div>
                    <Text variant={'display'}>Share {provider.user.first_name}'s profile</Text>
                </div>
                <Text variant="body1">
                    Share to Facebook, or copy a link to {provider.user.first_name}'s profile and email or text it to
                    your friends and family!
                </Text>
                <Grid container direction="row" justify="space-between" alignItems="center" style={{ marginTop: 30 }}>
                    <Grid item xs={4}>
                        <FacebookShareButton
                            quote={`I just found a great provider on Tandem! Check out their profile and book them for jobs here: `}
                            url={!!reid ? `${link}&reid=${reid}` : link}
                            children={
                                <Grid container direction="row" alignItems="center">
                                    <Text variant="body1">Share to</Text>
                                    <FacebookIcon size={30} borderRadius={30} style={{ marginLeft: 5 }} />
                                </Grid>
                            }
                            onClick={onFBShareClick}
                        />
                    </Grid>
                    <Grid container xs={7} sm={4}>
                        <PrimaryButton onClick={onCopyLinkClicked} loading={loading} disabled={loading}>
                            {copied ? 'Copied!' : 'Copy Link'}
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
